import { authService } from './authService'
import { logger } from '../utils/logger'

/**
 * Инициализация универсальной системы авторизации
 * 
 * Автоматически определяет режим работы приложения:
 * - Telegram WebApp: использует initData
 * - Browser: использует web_token из URL или localStorage
 * 
 * @returns Promise<boolean> - true если авторизация успешна
 */
export async function initAuth(): Promise<boolean> {
  logger.log('🚀 Начинаем инициализацию авторизации...')
  try {
    const result = await authService.initializeAuth()
    logger.log('✅ Инициализация авторизации завершена:', result)
    return result
  } catch (error) {
    logger.error('❌ Ошибка при инициализации авторизации:', error)
    return false
  }
}

/**
 * Проверка авторизации при каждом обновлении страницы
 * Используется в main.tsx для восстановления сессии
 */
/**
 * Проверяет и очищает несовместимые токены при смене режима
 */
function cleanupIncompatibleTokens(): void {
  // Определяем режим по окружению (приоритет Telegram окружения)
  const urlParams = new URLSearchParams(window.location.search)
  const userAgent = navigator.userAgent.toLowerCase()
  const pathname = window.location.pathname
  const hash = window.location.hash

  const webApp = (window as any)?.Telegram?.WebApp
  const hasWebAppObject = !!webApp
  const hasRealWebAppData = !!(webApp && (webApp.initDataUnsafe?.user?.id || (webApp.initData && webApp.initData.length > 0)))
  const hasTelegramUrlHints = !!(urlParams.get('tgWebAppPlatform') || urlParams.get('tgWebAppVersion') || hash.includes('tgWebAppData='))
  const isTelegramEnv = hasWebAppObject || hasRealWebAppData || hasTelegramUrlHints || userAgent.includes('telegram') || pathname.startsWith('/miniapp')

  const webTokenFromUrl = urlParams.get('web_token')
  const savedWebToken = localStorage.getItem('alfastil_web_token')
  const savedTelegramId = localStorage.getItem('telegram_id')
  const savedToken = localStorage.getItem('alfastil_token')

  if (isTelegramEnv) {
    // Находимся в Telegram окружении — очищаем браузерные данные
    if (process.env.NODE_ENV === 'development') {
      logger.log('🧹 Telegram окружение: очищаем браузерные токены')
    }
    localStorage.removeItem('alfastil_web_token')
    // РАНЬШЕ мы удаляли JWT web_auth в MiniApp. Теперь НЕ удаляем его, 
    // чтобы поддержать вход по email/паролю внутри MiniApp и последующую привязку telegram_id.
  } else {
    // Браузерное окружение — ОЧИЩАЕМ telegram_id!
    // Браузер НЕ ДОЛЖЕН иметь telegram_id - это только для Telegram режима
    if (savedTelegramId) {
      localStorage.removeItem('telegram_id')
      localStorage.removeItem('websocket_user_id') // Тоже очищаем
    }
    
    // Также очищаем Telegram-специфичные данные
    localStorage.removeItem('telegram_initData')
    
    if (process.env.NODE_ENV === 'development') {
      logger.log('🧹 Браузерное окружение: очищены все Telegram данные')
    }
  }
}

export async function checkAuthOnStartup(): Promise<boolean> {
  logger.log('🔍 ДИАГНОСТИКА: Проверяем авторизацию при запуске...')
  
  // ОТЛАДКА: Проверяем что есть в localStorage
  const savedToken = localStorage.getItem('alfastil_token')
  const savedUser = localStorage.getItem('alfastil_user')
  const telegramId = localStorage.getItem('telegram_id')
  
  logger.log('🔍 ДИАГНОСТИКА: localStorage при запуске', {
    hasToken: !!savedToken,
    hasUser: !!savedUser,
    telegramId,
    tokenStart: savedToken?.substring(0, 20),
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    userAgent: navigator.userAgent
  })
  try {
    // ВАЖНО: Сначала очищаем несовместимые токены
    cleanupIncompatibleTokens()
    
    // 🔥 КРИТИЧНО: Используем токен из authStore, а не из localStorage напрямую
    // authStore уже проверил и очистил истекшие токены при инициализации
    const { useAuthStore } = await import('./store')
    const currentAuthState = useAuthStore.getState()
    const savedToken = currentAuthState.token // Используем токен из store, не из localStorage!
    const savedUser = localStorage.getItem('alfastil_user')
    
    if (savedToken && savedUser) {
      if (process.env.NODE_ENV === 'development') {
        logger.log('🔄 Найден сохраненный токен, проверяем режим...')
      }
      
      // Определяем режим по сохраненным данным и текущему окружению
      const webToken = localStorage.getItem('alfastil_web_token')
      const telegramId = localStorage.getItem('telegram_id')
      
      let mode: 'telegram' | 'browser' = 'browser'
      
      // Используем ту же логику, что и в cleanupIncompatibleTokens
      const urlParams = new URLSearchParams(window.location.search)
      const webTokenFromUrl = urlParams.get('web_token')
      const hasWebToken = !!(webTokenFromUrl || webToken)
      
      if (hasWebToken) {
        mode = 'browser'
        if (process.env.NODE_ENV === 'development') {
          logger.log('🔍 Браузерный режим (web_token)')
        }
      } else if (telegramId) {
        mode = 'telegram'
        if (process.env.NODE_ENV === 'development') {
          logger.log('🔍 Telegram режим (telegram_id)')
        }
      } else {
        // Определяем по окружению
        const userAgent = navigator.userAgent.toLowerCase()
        const pathname = window.location.pathname
        const hash = window.location.hash
        
        const isTelegramEnv = userAgent.includes('telegram') ||
                             urlParams.get('tgWebAppPlatform') ||
                             urlParams.get('tgWebAppVersion') ||
                             hash.includes('tgWebAppData=') ||
                             pathname.startsWith('/miniapp')
        
        mode = isTelegramEnv ? 'telegram' : 'browser'
        if (process.env.NODE_ENV === 'development') {
          logger.log(`🔍 Режим по окружению: ${mode}`)
        }
      }
      
      // КРИТИЧНО: Для MiniApp НЕ восстанавливаем сессию из localStorage!
      // Всегда проверяем актуальный статус в Bitrix для безопасности
      if (mode === 'telegram') {
        if (process.env.NODE_ENV === 'development') {
          logger.log('🔐 MiniApp: НЕ восстанавливаем сессию - всегда проверяем Bitrix')
        }
        // Очищаем старый токен и запускаем полную инициализацию
        localStorage.removeItem('alfastil_token')
        localStorage.removeItem('alfastil_user')
        // НЕ возвращаем true - переходим к полной инициализации
      } else {
        // Для браузера восстанавливаем состояние авторизации
        const { useAuthStore } = await import('./store')
        const store = useAuthStore.getState()
        const user = JSON.parse(savedUser)
        if (process.env.NODE_ENV === 'development') {
          logger.log('🔄 Браузер: Восстанавливаем состояние авторизации:', { mode, user: user ? 'есть' : 'нет' })
        }
        // КРИТИЧНО: Устанавливаем telegram_id для WebSocket (для браузера используем contact_id)
        if (mode === 'browser' && user.contact_id && !localStorage.getItem('telegram_id')) {
          localStorage.setItem('telegram_id', String(user.contact_id))
          if (process.env.NODE_ENV === 'development') {
            logger.log('🔧 Установлен telegram_id для браузера:', user.contact_id)
          }
        }
        
        // 🔥 ИСПРАВЛЕНИЕ: Токен уже проверен в authStore при инициализации
        // Если мы дошли до этого места, значит токен валиден
        
        store.setAuth(savedToken, user, mode)
        
        // ВАЖНО: НЕ устанавливаем isCheckingAccess для восстановленной сессии
        // Пользователь уже авторизован, проверка доступа не нужна
        if (process.env.NODE_ENV === 'development') {
          logger.log('✅ Состояние после восстановления - пропускаем проверку доступа для восстановленной сессии')
        }
        
        if (process.env.NODE_ENV === 'development') {
          logger.log('✅ Браузерная сессия восстановлена')
        }
        return true
      }
    }
    
    // Если нет сохраненной сессии, запускаем полную инициализацию
    if (process.env.NODE_ENV === 'development') {
      logger.log('🆕 Нет сохраненной сессии, запускаем полную инициализацию...')
    }
    const result = await authService.initializeAuth()
    
    // ВАЖНО: Если инициализация не удалась, устанавливаем статус unauthenticated
    if (!result) {
      const { useAuthStore } = await import('./store')
      const store = useAuthStore.getState()
      store.setStatus('unauthenticated')
      logger.log('❌ Инициализация не удалась, статус: unauthenticated')
    }
    
    return result
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('❌ Ошибка при проверке авторизации:', error)
    }
    
    // ВАЖНО: В случае ошибки устанавливаем статус unauthenticated
    try {
      const { useAuthStore } = await import('./store')
      const store = useAuthStore.getState()
      store.setStatus('unauthenticated')
      store.setError('Ошибка при инициализации авторизации')
    } catch (storeError) {
      logger.error('❌ Ошибка при обновлении store:', storeError)
    }
    
    return false
  }
}

/**
 * Выход из системы
 */
export function logout(): void {
  authService.logout()
}